import { FC, ReactNode } from 'react'

import { match } from 'ts-pattern'
import {
  Box,
  Center,
  GreyGrey50,
  GreyGrey70,
  IconCheckCircle,
  IconPaperPlaneTopRight,
  Primary12Regular,
  SignerList
} from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'

import type { NotificationStatus } from './quoteRecipients.utils'

const TOOLTIP_DATE_FORMAT: string = 'd MMM, HH:mm'

const Wrapper: FC<{
  children: ReactNode
  'data-testid'?: string
}> = props => <SignerList {...props} />

const Item: FC<{
  name: string
  email: string
  notificationStatus: NotificationStatus
  signedAt: string | undefined
  lastNotifiedAt: string | undefined
}> = props => (
  <SignerList.Item
    name={props.name}
    email={props.email}
    icon={
      <NotificationStatusIcon
        notificationStatus={props.notificationStatus}
        data-testid={`quote.recipients.item.${props.email}.notificationIcon`}
      />
    }
    tooltipLabel={<RecipientTooltip {...props} />}
    data-testid={`quote.recipients.item.${props.email}`}
    data-notification-status={props.notificationStatus}
  />
)

/**
 * A recipient is one line in the list, so the detail lives here: how far they
 * have got - when they signed, or when we last emailed them - and their email
 * underneath it. Those dates are shown nowhere else in the sidebar. Nothing has
 * happened yet to somebody we haven't written to, so their email takes the top
 * line on its own.
 */
const RecipientTooltip: FC<{
  email: string
  notificationStatus: NotificationStatus
  signedAt: string | undefined
  lastNotifiedAt: string | undefined
}> = ({ email, notificationStatus, signedAt, lastNotifiedAt }) => {
  // Signing is as far as a recipient gets, so it takes the line even though
  // they were emailed to get there. Each date is still optional: the status is
  // derived from it, but the type can't say so.
  const progress = match(notificationStatus)
    .with('SIGNED', () =>
      signedAt ? `Signed ${format(signedAt, TOOLTIP_DATE_FORMAT)}` : undefined
    )
    .with('NOTIFIED', () =>
      lastNotifiedAt
        ? `Notified ${format(lastNotifiedAt, TOOLTIP_DATE_FORMAT)}`
        : undefined
    )
    .with('NOT_NOTIFIED', () => undefined)
    .exhaustive()

  // With no progress above it the email is the only line there is, so it reads
  // as the tooltip itself rather than as a caption under something else.
  const emailStyles = progress ? { ...Primary12Regular, color: GreyGrey50 } : {}

  return (
    <Box data-testid={`quote.recipients.item.${email}.tooltip`}>
      {progress && <Box paddingBottom="2px">{progress}</Box>}

      <Box
        {...emailStyles}
        data-testid={`quote.recipients.item.${email}.tooltip.email`}
      >
        {email}
      </Box>
    </Box>
  )
}

const NotificationStatusIcon: FC<{
  notificationStatus: NotificationStatus
  'data-testid': string
}> = ({ notificationStatus, 'data-testid': dataTestId }) => (
  <Center width="16px" height="16px" flexShrink="0" data-testid={dataTestId}>
    {match(notificationStatus)
      .with('NOT_NOTIFIED', () => <NotNotifiedIcon />)
      .with('NOTIFIED', () => <IconPaperPlaneTopRight color={GreyGrey70} />)
      .with('SIGNED', () => <IconCheckCircle color={GreyGrey70} />)
      .exhaustive()}
  </Center>
)

/**
 * Nothing has been emailed to this recipient yet, so the slot is drawn as an
 * outline of the icon that will take its place.
 */
const NotNotifiedIcon: FC = () => (
  <Box
    width="16px"
    height="16px"
    borderRadius="50%"
    border={`1px dashed ${GreyGrey50}`}
  />
)

export const QuoteRecipientList = Object.assign(Wrapper, {
  Item
})
